import React, { useState, useEffect } from 'react';
import { Gear, Check, Plus, X } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newCategory, setNewCategory] = useState('');

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try { const response = await axios.get(`${API_URL}/api/admin/ai-settings`); setSettings(response.data); }
    catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try { await axios.put(`${API_URL}/api/admin/ai-settings`, settings); toast.success('Paramètres enregistrés'); }
    catch (error) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const addCategory = () => {
    if (newCategory && !settings.enabled_categories.includes(newCategory)) {
      setSettings(p => ({ ...p, enabled_categories: [...p.enabled_categories, newCategory] }));
      setNewCategory('');
    }
  };

  const removeCategory = (cat) => setSettings(p => ({ ...p, enabled_categories: p.enabled_categories.filter(c => c !== cat) }));

  if (loading) return <AdminLayout title="Paramètres Analyse"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#E63946] border-t-transparent rounded-full animate-spin"></div></div></AdminLayout>;

  return (
    <AdminLayout title="Paramètres Analyse">
      <div className="max-w-3xl">
        <div className="bg-white border border-gray-200 p-6 mb-6 shadow-sm">
          <h2 className="font-['Outfit'] text-lg font-bold text-gray-900 mb-6 flex items-center gap-2"><Gear size={20} className="text-[#E63946]" />Configuration générale</h2>
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div><p className="text-gray-900 font-medium">Analyse automatique active</p><p className="text-gray-500 text-sm">Activer l'analyse automatique</p></div>
              <label className="relative inline-flex items-center cursor-pointer"><input type="checkbox" checked={settings?.active ?? true} onChange={(e) => setSettings(p => ({ ...p, active: e.target.checked }))} className="sr-only peer" /><div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E63946] rounded-full peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:h-5 after:w-5 after:rounded-full after:transition-all"></div></label>
            </div>
            <div>
              <label className="block text-gray-900 font-medium mb-2">Seuil de confiance minimum</label>
              <p className="text-gray-500 text-sm mb-3">Pièces sous ce seuil marquées "à vérifier"</p>
              <div className="flex items-center gap-4">
                <input type="range" min="0" max="100" value={settings?.min_confidence_threshold ?? 50} onChange={(e) => setSettings(p => ({ ...p, min_confidence_threshold: parseInt(e.target.value) }))} className="flex-1 h-2 bg-gray-200 appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-[#E63946] [&::-webkit-slider-thumb]:rounded-full" />
                <span className="text-gray-900 font-bold w-16 text-right">{settings?.min_confidence_threshold ?? 50}%</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 p-6 mb-6 shadow-sm">
          <h2 className="font-['Outfit'] text-lg font-bold text-gray-900 mb-4">Catégories de pièces actives</h2>
          <p className="text-gray-500 text-sm mb-4">Seules ces catégories seront suggérées par le système</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {settings?.enabled_categories?.map((cat, i) => (
              <span key={i} className="px-3 py-2 bg-gray-50 border border-gray-200 text-gray-700 text-sm flex items-center gap-2">{cat}<button onClick={() => removeCategory(cat)} className="text-gray-400 hover:text-[#E63946]"><X size={14} /></button></span>
            ))}
          </div>
          <div className="flex gap-2">
            <input type="text" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} placeholder="Nouvelle catégorie..." className="flex-1 bg-gray-50 border border-gray-300 text-gray-900 p-3" onKeyPress={(e) => e.key === 'Enter' && addCategory()} />
            <button onClick={addCategory} className="px-4 bg-gray-100 text-gray-700 hover:bg-gray-200"><Plus size={20} /></button>
          </div>
        </div>

        <div className="bg-white border border-gray-200 p-6 mb-6 shadow-sm">
          <h2 className="font-['Outfit'] text-lg font-bold text-gray-900 mb-4">Mapping des dommages</h2>
          <p className="text-gray-500 text-sm mb-4">Association dommages → pièces suggérées</p>
          <div className="space-y-4">
            {Object.entries(settings?.damage_mapping || {}).map(([type, parts], i) => (
              <div key={i} className="bg-gray-50 border border-gray-200 p-4">
                <h4 className="text-[#E63946] font-semibold text-sm uppercase tracking-wider mb-2">{type.replace(/_/g, ' ')}</h4>
                <div className="flex flex-wrap gap-1">{parts.map((p, j) => <span key={j} className="text-gray-600 text-xs bg-white border border-gray-200 px-2 py-1">{p}</span>)}</div>
              </div>
            ))}
          </div>
        </div>

        <button onClick={handleSave} disabled={saving} className="w-full py-4 bg-[#E63946] text-white text-sm font-bold uppercase hover:bg-[#D90429] disabled:opacity-50 flex items-center justify-center gap-2" data-testid="save-settings-btn">{saving ? <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>Enregistrement...</> : <><Check size={20} />Enregistrer</>}</button>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
